// src/context/PermissionContext.js
import { createContext, useContext, useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Navigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAttendanceMarked, setIsAttendanceMarked] = useState(false);
  const [assignedLocations, setAssignedLocations] = useState([]);
  const [usageStatistics, setUsageStatistics] = useState(true);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(
    localStorage.getItem("isExpired") === "true"
  );
  
  // Ref to store the last API response to avoid duplicate calls
  const lastPlanStatusResponse = useRef(null);
  const lastPlanStatusTime = useRef(0);
  const PLAN_STATUS_CACHE_TIME = 5 * 60 * 1000; // 5 minutes cache

  const checkAuth = () => {
    const token = localStorage.getItem("access_token");
    const expiryTime = localStorage.getItem("token_expiry");
    const currentTime = Date.now();
    return token && expiryTime && currentTime < parseInt(expiryTime, 10);
  };

  const fetchAttendanceStatus = async () => {
    try {
      const [attendanceResponse, locationResponse] = await Promise.all([
        apiCall("get", "/attendance-status"),
        apiCall("get", "/my-assigned-locations"),
      ]);

      const status = attendanceResponse.data.data?.status;
      const locations = Array.isArray(locationResponse.data.data)
        ? locationResponse.data.data
        : [];
      setIsAttendanceMarked(status === "CheckOut");
      setAssignedLocations(locations);
    } catch (error) {
      toast.error("Failed to fetch attendance status or locations.");
      setIsAttendanceMarked(false);
      setAssignedLocations([]);
    }
  };

  const checkSubscriptionStatus = async (forceRefresh = false) => {
    try {
      const currentTime = Date.now();
      
      // Check cache if not forcing refresh
      if (!forceRefresh && 
          lastPlanStatusResponse.current && 
          (currentTime - lastPlanStatusTime.current) < PLAN_STATUS_CACHE_TIME) {
        console.log("📦 Using cached plan status");
        const cachedData = lastPlanStatusResponse.current;
        processPlanStatusResponse(cachedData);
        return cachedData;
      }

      console.log("🔄 Fetching fresh plan status");
      const res = await apiCall("get", "/get-client-plan-status");
      
      // Cache the response
      lastPlanStatusResponse.current = res.data.data;
      lastPlanStatusTime.current = currentTime;
      
      processPlanStatusResponse(res.data.data);
      return res.data.data;
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return null;
    }
  };

  const processPlanStatusResponse = (planData) => {
    if (!planData) return;
    
    const { isExpired ,is_invoice_exceeded} = planData;
    
    // Update subscription status
    if (isExpired !== isSubscriptionExpired) {
      setIsSubscriptionExpired(isExpired);
      localStorage.setItem("isExpired", isExpired);
    }

    // Update usage statistics based on payment info
    let isPaymentValid = false;
    // if (last_payment_date) {
    //   const today = new Date();
    //   const currentDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    //   const amountDue = parseFloat(last_amount_due);
    //   if (last_payment_date <= currentDate && amountDue > 0 && last_invoice_status === "sent") {
    //     isPaymentValid = true;
    //   }
    // }
    if(is_invoice_exceeded){
        isPaymentValid = true;
      }
    setUsageStatistics(isPaymentValid);
  };

  const refreshAuthStatus = async () => {
    setIsLoading(true);
    const authStatus = checkAuth();
    setIsAuthenticated(authStatus);

    if (!authStatus) {
      setPermissions([]);
      setIsAttendanceMarked(false);
      setAssignedLocations([]);
      setIsSubscriptionExpired(false);
      setUsageStatistics(true);
      localStorage.removeItem("isExpired");
      lastPlanStatusResponse.current = null;
      lastPlanStatusTime.current = 0;
      setIsLoading(false);
      return <Navigate to="/sign-in" replace />;
    }

    try {
      // Fetch plan status first
      const planStatus = await checkSubscriptionStatus();
      
      // Then fetch permissions in parallel with attendance if needed
      const promises = [
        apiCall("get", "/get-all-assigned-permissions?want_pagination=0"),
      ];

      // Only fetch attendance if subscription is not expired
      if (!planStatus?.isExpired) {
        promises.push(fetchAttendanceStatus());
      }

      const [permissionResponse] = await Promise.all(promises);

      const fetchedPermissions = Array.isArray(permissionResponse.data.data)
        ? permissionResponse.data.data
        : [];
      setPermissions(fetchedPermissions);
      setIsAuthenticated(permissionResponse.data.success || false);
    } catch (error) {
      console.error("Error fetching permissions or attendance:", error);
      setIsAuthenticated(false);
      setPermissions([]);
      setIsAttendanceMarked(false);
      setAssignedLocations([]);
      setIsSubscriptionExpired(false);
      setUsageStatistics(true);
      lastPlanStatusResponse.current = null;
      lastPlanStatusTime.current = 0;
      localStorage.clear();
      toast.error("Failed to load permissions or attendance. Please log in again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Initial check
      checkSubscriptionStatus();
      
      const interval = setInterval(() => {
        if (isAuthenticated && !isLoading) {
          checkSubscriptionStatus();
        }
      }, 1440 * 60 * 1000); // 30 minutes

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    refreshAuthStatus();
  }, []);

  const hasPermission = (permissionKey) => {
    return (
      Array.isArray(permissions) &&
      permissions.some((perm) => perm?.permission_key === permissionKey)
    );
  };

  // Function to force refresh plan status when needed (e.g., after payment)
  const refreshPlanStatus = async () => {
    return await checkSubscriptionStatus(true);
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        hasPermission,
        isAuthenticated,
        isLoading,
        refreshAuthStatus,
        isAttendanceMarked,
        setIsAttendanceMarked,
        fetchAttendanceStatus,
        assignedLocations,
        isSubscriptionExpired,
        checkSubscriptionStatus: () => checkSubscriptionStatus(false),
        refreshPlanStatus, // Add this for forced refresh
        usageStatistics,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);